import type { Contract, providers } from 'ethers';
import { useEffect, useState } from 'react';
import { isAddress } from 'viem';
import { useWalletClient } from 'wagmi';

import type * as Factories from '../lib/contracts';
import { walletClientToSigner } from '../lib/smart-contract-utils';
import { useWeb3State } from './use-web3-state';

interface ContractAddressMap {
  [key: string]: string;
}

export const useContract = <T extends Contract>(
  contractFactoryClass: typeof Factories[keyof typeof Factories],
  contractAddressMap: ContractAddressMap,
  contractAddressOverride = '',
  overrideProvider = '',
  hardOverrideProvider = false,
) => {
  const { walletDetails } = useWeb3State();
  const [contract, setContract] = useState<T>();
  const walletClient = useWalletClient();

  useEffect(() => {
    if (!walletClient.data) {
      return;
    }

    const signer = walletClientToSigner(walletClient.data);
    const address =
      contractAddressOverride || contractAddressMap[walletDetails.chain.type];

    if (!address || !isAddress(address)) {
      return;
    }

    setContract(contractFactoryClass.connect(address, signer) as unknown as T);
  }, [contractAddressMap, contractAddressOverride, walletClient.data]);

  return {
    ...walletDetails,
    contract,
    connect: (provider: providers.Web3Provider) => {
      setContract(
        contractFactoryClass.connect(
          contractAddressMap[walletDetails.address ?? ''],
          provider,
        ) as unknown as T,
      );
    },
    checkForMethodTrx: async (methodSig: string) => {
      if (!contract) {
        return;
      }

      const trx = localStorage.getItem(methodSig) ?? '';

      try {
        await contract.provider.getTransaction(trx);
      } catch (e) {
        localStorage.removeItem(methodSig);
      }
    },
  };
};
